import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Slider } from '../components/ui/slider';
import { 
    ArrowLeft, Loader2, Check, Sparkles, Plus, Trash2, ListChecks
} from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { SENDAFooter } from '../components/SENDAFooter';
import { ModuleFeedback } from '../components/ModuleFeedback';
import { ReadOnlyBanner } from '../components/ReadOnlyBanner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIPOS_TAREA = ['Laboral', 'Personal', 'Académico', 'Social', 'Doméstico', 'Salud'];
const FRECUENCIAS = ['Diaria', 'Semanal', 'Mensual', 'Esporádica'];

const emptyTask = () => ({ nombre: '', tipo: 'Laboral', frecuencia: 'Semanal', carga_mental: 5, progreso: 3, clasificacion: 'central' });

export default function Modulo31InventarioFrentes() {
    const [tareas, setTareas] = useState(Array.from({ length: 8 }, emptyTask));
    const [loading, setLoading] = useState(false);
    const [savingDraft, setSavingDraft] = useState(false);
    const [loadingDraft, setLoadingDraft] = useState(true);
    const [readOnly, setReadOnly] = useState(false);
    const [submittedAt, setSubmittedAt] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [aiInsight, setAiInsight] = useState('');

    useEffect(() => {
        const loadDraft = async () => {
            try {
                const { data } = await axios.get(`${API_URL}/api/user/draft/inventario_frentes`, { withCredentials: true });
                if (data.data?.tareas?.length) setTareas(data.data.tareas);
                if (data.read_only) { setReadOnly(true); setSubmittedAt(data.submitted_at); }
            } catch { /* no draft */ } finally { setLoadingDraft(false); }
        };
        loadDraft();
    }, []);

    const saveDraft = useCallback(async () => {
        setSavingDraft(true);
        try {
            await axios.post(`${API_URL}/api/user/draft/save`, { module_id: 'inventario_frentes', data: { tareas } }, { withCredentials: true });
        } catch { /* silent */ } finally { setSavingDraft(false); }
    }, [tareas]);

    useEffect(() => {
        if (loadingDraft || readOnly) return;
        const timer = setTimeout(saveDraft, 2000);
        return () => clearTimeout(timer);
    }, [tareas, saveDraft, loadingDraft, readOnly]);

    const updateTask = (index, field, value) => {
        setTareas(prev => prev.map((t, i) => i === index ? { ...t, [field]: value } : t));
    };

    const addTask = () => {
        if (tareas.length >= 15) { toast.error('Máximo 15 tareas'); return; }
        setTareas(prev => [...prev, emptyTask()]);
    };

    const removeTask = (index) => {
        if (tareas.length <= 8) { toast.error('Mínimo 8 tareas'); return; }
        setTareas(prev => prev.filter((_, i) => i !== index));
    };

    const filledTasks = tareas.filter(t => t.nombre.trim().length > 0);
    const canSubmit = !readOnly && (filledTasks.length >= 8);

    const handleSubmit = async () => {
        if (readOnly) return;
        if (!canSubmit) { toast.error('Completa al menos 8 tareas con nombre'); return; }
        setLoading(true);
        try {
            const { data } = await axios.post(`${API_URL}/api/user/modules/inventario-frentes/submit`,
                { tareas: filledTasks.map(t => ({ ...t, carga_mental: typeof t.carga_mental === 'object' ? t.carga_mental[0] : t.carga_mental, progreso: typeof t.progreso === 'object' ? t.progreso[0] : t.progreso })) },
                { withCredentials: true });
            setAiInsight(data.ai_insight);
            setSubmitted(true);
            toast.success('Módulo completado');
        } catch (error) { toast.error(error.response?.data?.detail || 'Error al enviar'); }
        finally { setLoading(false); }
    };

    if (loadingDraft) return <div className="min-h-screen bg-[#F5F7F8] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#3E5F6E]" /></div>;

    if (submitted) {
        return (
            <div className="min-h-screen bg-[#F5F7F8] flex items-center justify-center p-4">
                <Toaster position="top-right" richColors />
                <Card className="w-full max-w-xl bg-white border border-[#DCE3E8] rounded-2xl shadow-xl">
                    <CardContent className="pt-8 text-center">
                        <div className="w-20 h-20 bg-gradient-to-br from-[#5FAF9D] to-[#4A9A89] rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                            <ListChecks className="h-10 w-10 text-white" />
                        </div>
                        <h2 className="text-2xl font-light text-[#1F2A30] font-['Outfit'] mb-2">Inventario registrado</h2>
                        <p className="text-[#5FAF9D] font-medium mb-4">{filledTasks.length} frentes abiertos mapeados</p>
                        {aiInsight && (
                            <div className="bg-[#F5F7F8] border-l-4 border-[#5FAF9D] p-4 rounded-r-xl text-left mb-6">
                                <div className="flex items-start gap-3">
                                    <Sparkles className="h-5 w-5 text-[#5FAF9D] shrink-0 mt-0.5" />
                                    <p className="text-[#1F2A30] italic text-sm">"{aiInsight}"</p>
                                </div>
                            </div>
                        )}
                        <div className="mb-6"><ModuleFeedback moduleId="inventario_frentes" chapter={3} /></div>
                    <Link to="/usuario"><Button className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white px-8 py-3 rounded-xl">Continuar</Button></Link>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F5F7F8]">
            <Toaster position="top-right" richColors />
            <header className="bg-white/90 backdrop-blur-xl border-b border-[#DCE3E8]/50 sticky top-0 z-50">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <Link to="/usuario"><Button variant="ghost" size="icon" className="text-[#3E5F6E]"><ArrowLeft className="h-5 w-5" /></Button></Link>
                            <div>
                                <div className="flex items-center gap-2">
                                    <span className="text-xs font-semibold text-[#5FAF9D] uppercase tracking-wide">Capítulo 3</span>
                                    <span className="text-xs text-[#7A8A93]">Módulo 3.1</span>
                                </div>
                                <h1 className="text-lg font-semibold text-[#1F2A30] font-['Outfit']">Inventario de frentes abiertos</h1>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            {savingDraft && <Loader2 className="h-3 w-3 animate-spin text-[#7A8A93]" />}
                            <span className="text-sm text-[#7A8A93]">{filledTasks.length}/8 mín.</span>
                        </div>
                    </div>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                                {readOnly && <ReadOnlyBanner submittedAt={submittedAt} />}
                <Card className="bg-white border border-[#DCE3E8] rounded-3xl shadow-sm overflow-hidden">
                    <CardHeader className="bg-gradient-to-r from-[#3E5F6E]/5 to-transparent p-6">
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-gradient-to-br from-[#3E5F6E] to-[#2F4A57] rounded-xl flex items-center justify-center shadow-md">
                                <ListChecks className="h-6 w-6 text-white" />
                            </div>
                            <div>
                                <CardTitle className="text-[#1F2A30]">¿En cuántos frentes estás?</CardTitle>
                                <p className="text-sm text-[#4A5B63]">Registra todas las tareas, proyectos y compromisos que ocupan tu mente</p>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="p-6">
                        <div className="overflow-x-auto">
                            <div className="space-y-4 min-w-[700px]">
                                {/* Header row */}
                                <div className="grid grid-cols-12 gap-2 text-xs font-semibold text-[#3E5F6E] uppercase pb-2 border-b border-[#DCE3E8]">
                                    <div className="col-span-3">Tarea</div>
                                    <div className="col-span-1">Tipo</div>
                                    <div className="col-span-1">Frec.</div>
                                    <div className="col-span-2 text-center">Carga mental</div>
                                    <div className="col-span-2 text-center">Progreso</div>
                                    <div className="col-span-2">Clasif.</div>
                                    <div className="col-span-1"></div>
                                </div>
                                {tareas.map((tarea, idx) => (
                                    <div key={idx} className="grid grid-cols-12 gap-2 items-center py-2 border-b border-[#F0F2F4]" data-testid={`task-row-${idx}`}>
                                        <div className="col-span-3">
                                            <input type="text" value={tarea.nombre} onChange={e => updateTask(idx, 'nombre', e.target.value)}
                                                placeholder={`Tarea ${idx + 1}`}
                                                className="w-full px-3 py-2 text-sm border border-[#DCE3E8] rounded-lg bg-[#F5F7F8] focus:ring-1 focus:ring-[#5FAF9D] focus:border-[#5FAF9D] outline-none"
                                                data-testid={`task-name-${idx}`} />
                                        </div>
                                        <div className="col-span-1">
                                            <select value={tarea.tipo} onChange={e => updateTask(idx, 'tipo', e.target.value)}
                                                className="w-full px-1 py-2 text-xs border border-[#DCE3E8] rounded-lg bg-[#F5F7F8] outline-none">
                                                {TIPOS_TAREA.map(t => <option key={t} value={t}>{t}</option>)}
                                            </select>
                                        </div>
                                        <div className="col-span-1">
                                            <select value={tarea.frecuencia} onChange={e => updateTask(idx, 'frecuencia', e.target.value)}
                                                className="w-full px-1 py-2 text-xs border border-[#DCE3E8] rounded-lg bg-[#F5F7F8] outline-none">
                                                {FRECUENCIAS.map(f => <option key={f} value={f}>{f}</option>)}
                                            </select>
                                        </div>
                                        <div className="col-span-2 flex items-center gap-2">
                                            <Slider value={[typeof tarea.carga_mental === 'object' ? tarea.carga_mental[0] : tarea.carga_mental]}
                                                onValueChange={v => updateTask(idx, 'carga_mental', v[0])} max={10} min={0} step={1} className="flex-1" />
                                            <span className="text-xs font-bold text-[#3E5F6E] w-5 text-right">{typeof tarea.carga_mental === 'object' ? tarea.carga_mental[0] : tarea.carga_mental}</span>
                                        </div>
                                        <div className="col-span-2 flex items-center gap-2">
                                            <Slider value={[typeof tarea.progreso === 'object' ? tarea.progreso[0] : tarea.progreso]}
                                                onValueChange={v => updateTask(idx, 'progreso', v[0])} max={10} min={0} step={1} className="flex-1" />
                                            <span className="text-xs font-bold text-[#5FAF9D] w-5 text-right">{typeof tarea.progreso === 'object' ? tarea.progreso[0] : tarea.progreso}</span>
                                        </div>
                                        <div className="col-span-2">
                                            <div className="flex gap-1">
                                                <button onClick={() => updateTask(idx, 'clasificacion', 'central')}
                                                    className={`px-2 py-1 text-xs rounded-md transition-all ${tarea.clasificacion === 'central' ? 'bg-[#3E5F6E] text-white' : 'bg-[#F5F7F8] text-[#7A8A93] border border-[#DCE3E8]'}`}>
                                                    Central
                                                </button>
                                                <button onClick={() => updateTask(idx, 'clasificacion', 'periferica')}
                                                    className={`px-2 py-1 text-xs rounded-md transition-all ${tarea.clasificacion === 'periferica' ? 'bg-[#E8B27A] text-white' : 'bg-[#F5F7F8] text-[#7A8A93] border border-[#DCE3E8]'}`}>
                                                    Perif.
                                                </button>
                                            </div>
                                        </div>
                                        <div className="col-span-1 text-center">
                                            {tareas.length > 8 && (
                                                <button onClick={() => removeTask(idx)} className="text-[#D07F7E] hover:text-[#B06060] transition-colors">
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="flex items-center justify-between mt-6 pt-6 border-t border-[#DCE3E8]">
                            <Button onClick={addTask} variant="outline" disabled={tareas.length >= 15}
                                className="border-[#DCE3E8] text-[#3E5F6E]" data-testid="add-task-btn">
                                <Plus className="h-4 w-4 mr-2" /> Agregar tarea
                            </Button>
                            <Button onClick={handleSubmit} disabled={!canSubmit || loading}
                                className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white px-8 py-3 rounded-xl shadow-md"
                                data-testid="submit-inventario">
                                {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Procesando...</> : <><Check className="h-4 w-4 mr-2" />Completar módulo</>}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </main>
            <SENDAFooter />
        </div>
    );
}
